import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  IconButton,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  useCreatePolicyTermsMutation,
  useDeletePolicyTermsMutation,
} from "../../../Api/attoDeskApi";
import {
  IPolicyTerm,
  policyTermsArray,
} from "../../../Api/Interface/api.interface";
import { useFormik } from "formik";
import CustomSwitch from "../../../Components/Switch/CustomSwitch";
import { useStyles } from "../../../theme/appStyles";
import CloseIcon from "@mui/icons-material/Close";
import { useNotifier } from "../../../Core/Notifier";
import { uploadPDF } from "../../../Components/UploadPdf/fillPDF";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AddIcon from "@mui/icons-material/Add";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import Switch, { SwitchProps } from "@mui/material/Switch";
import { FaTrash } from "react-icons/fa";
import Delete from "@mui/icons-material/Delete";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

interface LoopingTableProps {
  productId: string;
  loopingTableComplete?: () => void;
  data?: IPolicyTerm;
  handleLoopingClose: (close: boolean) => void;
}

const IOSSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 50,
  height: 26,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(22px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: theme.palette.primary,
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#33cf4d",
      border: "4px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color:
        theme.palette.mode === "light"
          ? theme.palette.grey[100]
          : theme.palette.grey[600],
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: theme.palette.mode === "light" ? 0.7 : 0.3,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 24,
    height: 24,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === "light" ? "#757575" : "#39393D",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
    "&::before, &::after": {
      content: '""',
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      width: 12,
      height: 12,
    },
    "&::before": {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="12" width="12" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main)
      )}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
      left: 8,
    },
    "&::after": {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="12" width="12" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main)
      )}" d="M19,13H5V11H19V13Z" /></svg>')`,
      right: 8,
    },
  },
}));

const LoopingTable: React.FC<LoopingTableProps> = ({
  productId,
  loopingTableComplete,
  data,
  handleLoopingClose,
}) => {
  const classes = useStyles();
  const { showErrorMessage, showMessage } = useNotifier();
  const [policyTermsData, { isLoading: loopingDataLoading }] =
    useCreatePolicyTermsMutation();

  const [selectedPdf, setSelectedPdf] = useState<File | undefined>(undefined);
  const [isPdfUploading, setIsPdfUploading] = useState(false);

  const handleClose = () => {
    handleLoopingClose(false);
  };

  const [currentTerm, setCurrentTerm] = useState<policyTermsArray[]>(
    data?.policyTermsArray || []
  );

  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const formik = useFormik({
    initialValues: {
      id: data?.id,
      policyTermProductId: productId,
      isPolicyRenerwalReminder: data?.isPolicyRenerwalReminder || true,
      policyRenerwalReminderDayBefore:
        data?.policyRenerwalReminderDayBefore || "",
      policyTerms: [],
      policyTermsDocument: data?.policyTermsDocument || "",
    },

    onSubmit: async (values, { resetForm }) => {
      try {
        const formData = new FormData();
        let policyTermsDocument: string | undefined = data?.policyTermsDocument;

        if (selectedPdf) {
          setIsPdfUploading(true);
          formData.append("policyTermsDocument", selectedPdf);
          policyTermsDocument = (await uploadPDF(selectedPdf)) || "";
          setIsPdfUploading(false);
        }
        const temData = {
          id: values.id,
          policyTermProductId: values.policyTermProductId,
          isPolicyRenerwalReminder: values.isPolicyRenerwalReminder,
          policyRenerwalReminderDayBefore:
            values.policyRenerwalReminderDayBefore,
          policyTermsArray: currentTerm,
          policyTermsDocument,
        };

        const addCompanyResponse = await policyTermsData(temData).unwrap();
        if (!addCompanyResponse.status) {
          showErrorMessage(addCompanyResponse.message);
        } else {
          showMessage(addCompanyResponse.message);
          resetForm();
          if (loopingTableComplete) {
            loopingTableComplete();
          }
          handleClose();
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const handlePdfChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    if (file && file.type === "application/pdf") {
      setSelectedPdf(file);
    } else {
      showErrorMessage("Please select a valid PDF file");
    }
  };

  const handleAddRow = () => {
    setCurrentTerm((prev) => [
      ...prev,
      {
        id: null,
        policyTermsDescription: "",
        isPolicyTermsSummaryReport: true,
        isPolicyTermsAll: true,
        isPolicyTermsPrintPdf: true,
        termsOrderNumber: null,
      },
    ]);
  };

  // const handleFieldChange = <K extends keyof policyTermsArray>(
  //   index: number,
  //   field: K,
  //   value: policyTermsArray[K]
  // ) => {
  //   const updatedRows = currentTerm.map((term, i) => {
  //     if (i === index) {
  //       return {
  //         ...term,
  //         [field]: value !== undefined ? value : term[field],
  //       };
  //     }
  //     return term;
  //   });

  //   setCurrentTerm(updatedRows);
  // };

  const handleFieldChange = <K extends keyof policyTermsArray>(
    index: number,
    field: K,
    value: policyTermsArray[K]
  ) => {
    // const updatedRows = [...currentTerm];
    const updatedRows = currentTerm.map((row) => ({ ...row }));

    // Update the main field
    updatedRows[index][field] = value;

    // If "isPolicyTermsAll" is being turned on, set the others to true
    if (field === "isPolicyTermsAll" && value === true) {
      updatedRows[index].isPolicyTermsSummaryReport = true;
      updatedRows[index].isPolicyTermsPrintPdf = true;
    }

    setCurrentTerm(updatedRows);
  };

  // const handleDeleteRow = (index: number) => {
  //   const updatedRows = currentTerm.filter((_, i) => i !== index);
  //   setCurrentTerm(updatedRows);
  // };

  const [deleteCategory] = useDeletePolicyTermsMutation();

  const handleDeleteRow = async () => {
    if (deleteIndex === null) return;
    const id = currentTerm[deleteIndex]?.id;
    if (id !== null) {
      try {
        await deleteCategory(id.toString()).unwrap();
        setCurrentTerm((prev) => prev.filter((_, i) => i !== deleteIndex));
        setDeleteIndex(null);
        setIsDialogOpen(false);
      } catch (error) {
        console.error("Failed to delete policy term:", error);
        alert("Error deleting policy term. Please try again.");
      }
    } else {
      setCurrentTerm((prev) => prev.filter((_, i) => i !== deleteIndex));
      setDeleteIndex(null);
      setIsDialogOpen(false);
    }
  };

  const openDeleteDialog = (index: number) => {
    setDeleteIndex(index);
    setIsDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteIndex(null);
    setIsDialogOpen(false);
  };

  return (
    <div>
      <Grid container spacing={3}>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Card className={classes.card}>
            <Grid
              container
              justifyContent={"space-between"}
              spacing={5}
              sx={{ borderBottom: 2, padding: 2 }}
              className={classes.titleGrid}
            >
              <Grid
                item
                lg={12}
                md={12}
                sm={12}
                xs={12}
                pt={2}
                alignContent={"center"}
              >
                <Typography className={classes.cardTitle}>Renewal</Typography>
              </Grid>
            </Grid>

            <Grid container spacing={2} p={"24px"}>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Grid container alignItems="center">
                  <Grid item lg={2} md={23} sm={12} xs={12}>
                    <Typography variant="subtitle1">
                      Renewal Reminder
                    </Typography>
                  </Grid>
                  <Grid item lg={10} md={10} sm={12} xs={12}>
                    <CustomSwitch
                      color="primary"
                      sx={{ mr: 2 }}
                      formik={formik}
                      name="isPolicyRenerwalReminder"
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item lg={4} md={4} sm={12} xs={12}>
                <Grid container alignItems="center">
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Typography variant="subtitle1">Days Before</Typography>
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <TextField
                      placeholder="Enter Days Before"
                      size="small"
                      {...formik.getFieldProps(
                        "policyRenerwalReminderDayBefore"
                      )}
                      className={classes.textField}
                      InputProps={{
                        sx: {
                          fontSize: 14,
                        },
                      }}
                      InputLabelProps={{
                        sx: {
                          fontSize: 14,
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Card className={classes.card}>
            <Grid
              container
              justifyContent={"space-between"}
              spacing={5}
              sx={{ borderBottom: 2, padding: 2 }}
              className={classes.titleGrid}
            >
              <Grid
                item
                lg={12}
                md={12}
                sm={12}
                xs={12}
                pt={2}
                alignContent={"center"}
              >
                <Typography className={classes.cardTitle}>Terms</Typography>
              </Grid>
            </Grid>
            {currentTerm.map((product, index) => (
              <Grid container spacing={2} p={"24px"} key={index}>
                <Grid item lg={8} md={8} sm={12} xs={12}>
                  <ReactQuill
                    value={product.policyTermsDescription}
                    onChange={(newValue) =>
                      handleFieldChange(
                        index,
                        "policyTermsDescription",
                        newValue
                      )
                    }
                    placeholder="Enter Terms..."
                    theme="snow"
                    style={{ height: "200px" }} 
                    modules={{
                      toolbar: [
                        [{ font: [] }],
                        [{ header: [1, 2, 3, 4, 5, 6, false] }],
                        ["bold", "italic", "underline", "strike"],
                        [{ color: [] }, { background: [] }],
                        [{ script: "sub" }, { script: "super" }],
                        [{ list: "ordered" }, { list: "bullet" }],
                        [{ indent: "-1" }, { indent: "+1" }],
                        [{ direction: "rtl" }],
                        [{ align: [] }],
                        ["blockquote", "code-block"],
                        ["link", "image", "video"],
                        ["clean"],
                      ],
                    }}
                  />
                  {/* <TextField
                    placeholder="Enter Terms"
                    size="small"
                    value={product.policyTermsDescription}
                    onChange={(e) =>
                      handleFieldChange(
                        index,
                        "policyTermsDescription",
                        String(e.target.value)
                      )
                    }
                    multiline
                    rows={8}
                    className={classes.textField}
                    InputProps={{
                      sx: {
                        fontSize: 14,
                      },
                    }}
                    InputLabelProps={{
                      sx: {
                        fontSize: 14,
                      },
                    }}
                  /> */}
                </Grid>
                <Grid item lg={4} md={4} sm={12} xs={12}>
                  <Grid container spacing={2}>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid container>
                        <Grid item lg={3} md={3} sm={6} xs={6}>
                          <IOSSwitch
                            color="primary"
                            sx={{ mr: 2 }}
                            checked={product.isPolicyTermsSummaryReport}
                            onChange={(e) =>
                              handleFieldChange(
                                index,
                                "isPolicyTermsSummaryReport",
                                Boolean(e.target.checked)
                              )
                            }
                          />
                        </Grid>
                        <Grid item lg={9} md={9} sm={6} xs={6}>
                          <Typography variant="subtitle1">
                            Summary Report
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid container>
                        <Grid item lg={3} md={3} sm={6} xs={6}>
                          <IOSSwitch
                            color="primary"
                            sx={{ mr: 2 }}
                            checked={product.isPolicyTermsAll}
                            onChange={(e) =>
                              handleFieldChange(
                                index,
                                "isPolicyTermsAll",
                                Boolean(e.target.checked)
                              )
                            }
                          />
                        </Grid>
                        <Grid item lg={9} md={9} sm={6} xs={6}>
                          <Typography variant="subtitle1">All</Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid container>
                        <Grid item lg={3} md={3} sm={6} xs={6}>
                          <IOSSwitch
                            color="primary"
                            sx={{ mr: 2 }}
                            checked={product.isPolicyTermsPrintPdf}
                            onChange={(e) =>
                              handleFieldChange(
                                index,
                                "isPolicyTermsPrintPdf",
                                Boolean(e.target.checked)
                              )
                            }
                          />
                        </Grid>
                        <Grid item lg={9} md={9} sm={6} xs={6}>
                          <Typography variant="subtitle1">Print-PDF</Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid container>
                        <Grid item lg={3} md={4} sm={6} xs={6} pr={2}>
                          <TextField
                            placeholder="Number"
                            size="small"
                            type="number"
                            value={product.termsOrderNumber}
                            onChange={(e) =>
                              handleFieldChange(
                                index,
                                "termsOrderNumber",
                                Number(e.target.value)
                              )
                            }
                            className={classes.textField}
                            InputProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                            InputLabelProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                          />
                        </Grid>
                        <Grid item lg={9} md={8} sm={6} xs={6}>
                          <Typography variant="subtitle1">
                            Order Number
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <IconButton
                        color="error"
                        onClick={() => openDeleteDialog(index)}
                      >
                        <FaTrash />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            ))}
            <Grid container>
              <Grid
                item
                lg={8}
                md={8}
                sm={12}
                xs={12}
                py={2}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant="contained"
                  className={classes.editButton}
                  onClick={handleAddRow}
                >
                  <AddIcon />
                </Button>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Card className={classes.card}>
            <Grid
              container
              justifyContent={"space-between"}
              spacing={5}
              sx={{ borderBottom: 2, padding: 2 }}
              className={classes.titleGrid}
            >
              <Grid
                item
                lg={12}
                md={12}
                sm={12}
                xs={12}
                pt={2}
                alignContent={"center"}
              >
                <Typography className={classes.cardTitle}>
                  Upload Document (PDF)
                </Typography>
              </Grid>
            </Grid>

            <Grid container spacing={2} p={"24px"}>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Box className={classes.imgBox}>
                  {selectedPdf && (
                    <Typography>Selected PDF: {selectedPdf.name}</Typography>
                  )}
                  {!selectedPdf && data && data.policyTermsDocument ? (
                    <Typography>
                      Current PDF:{" "}
                      <a
                        href={data.policyTermsDocument}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View Proposal
                      </a>
                    </Typography>
                  ) : (
                    <></>
                  )}
                  <div>
                    <input
                      accept="application/pdf"
                      style={{ display: "none" }}
                      id="icon-button-file-pdf"
                      type="file"
                      onChange={handlePdfChange}
                    />
                    <div style={{ textAlign: "center" }}>
                      <label htmlFor="icon-button-file-pdf">
                        <IconButton
                          color="primary"
                          aria-label="upload PDF"
                          component="span"
                        >
                          <CloudUploadIcon sx={{ fontSize: 45 }} />
                        </IconButton>
                      </label>
                      <div>Only PDF files are allowed</div>
                    </div>
                  </div>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
          <Card className={classes.card}>
            <Grid container spacing={3} p={"24px"}>
              <Grid
                item
                lg={12}
                md={12}
                sm={12}
                xs={12}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Button
                  variant="outlined"
                  startIcon={<CloseIcon />}
                  className={classes.addButton}
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                <Box m={0.5}></Box>
                <Button
                  variant="contained"
                  startIcon={<CheckCircleOutlineIcon />}
                  className={classes.addButton}
                  onClick={() => formik.handleSubmit()}
                >
                  Save
                </Button>
              </Grid>
            </Grid>
          </Card>
        </Grid>
        <Dialog
          open={isDialogOpen}
          onClose={closeDeleteDialog}
          sx={{ padding: 5, minWidth: 450 }}
        >
          <Grid
            container
            justifyContent={"space-between"}
            spacing={5}
            sx={{ borderBottom: 2, padding: 2 }}
            className={classes.titleGrid}
          >
            <Grid
              item
              lg={12}
              md={12}
              sm={12}
              xs={12}
              pt={2}
              alignContent={"center"}
            >
              <Typography className={classes.cardTitle}>
                Delete Policy Term
              </Typography>
            </Grid>
          </Grid>
          <DialogContent>
            <DialogContentText sx={{ pt: 3 }}>
              Are you sure you want to delete this policy term?
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ paddingRight: "24px", p: 4, py: 3, gap: 2 }}>
            <Grid container spacing={3}>
              <Grid
                item
                lg={12}
                md={12}
                sm={12}
                xs={12}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Button
                  variant="outlined"
                  startIcon={<CloseIcon />}
                  className={classes.addButton}
                  onClick={closeDeleteDialog}
                >
                  Cancel
                </Button>
                <Box m={0.5}></Box>

                <Button
                  variant="contained"
                  startIcon={<Delete />}
                  className={classes.addButton}
                  onClick={handleDeleteRow}
                >
                  Confirm
                </Button>
              </Grid>
            </Grid>
          </DialogActions>
        </Dialog>
      </Grid>
    </div>
  );
};

export default LoopingTable;
